import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui';
import { useAppDispatch } from '../store/hooks';
import { addNotification } from '../store/slices/uiSlice';
import { courrierService } from '../services/api';
import { queryKeys } from '../lib/react-query';
import { CourrierStatus } from '../types/api';

interface WorkflowTransitionProps {
  courrierId: number;
  currentStatus: CourrierStatus;
  onTransition?: (newStatus: CourrierStatus) => void;
  disabled?: boolean;
}

const STATUS_TRANSITIONS: Record<CourrierStatus, CourrierStatus[]> = {
  RECU: ['EN_VALIDATION', 'INCOMPLET', 'REJETE'],
  EN_VALIDATION: ['SIGNE', 'INCOMPLET', 'REJETE'],
  INCOMPLET: ['EN_VALIDATION', 'REJETE'],
  EN_ATTENTE_SIGNATURE: ['SIGNE', 'REJETE'],
  SIGNE: ['ENVOYE', 'REJETE'],
  ENVOYE: ['CLASSE'],
  CLASSE: [], // État final
  REJETE: [], // État final
};

const STATUS_LABELS: Record<CourrierStatus, string> = {
  RECU: 'Reçu',
  EN_VALIDATION: 'En validation',
  INCOMPLET: 'Incomplet',
  EN_ATTENTE_SIGNATURE: 'En attente signature',
  SIGNE: 'Signé',
  ENVOYE: 'Envoyé',
  CLASSE: 'Classé',
  REJETE: 'Rejeté',
};

const STATUS_COLORS: Record<CourrierStatus, string> = {
  RECU: 'bg-blue-100 text-blue-800',
  EN_VALIDATION: 'bg-yellow-100 text-yellow-800',
  INCOMPLET: 'bg-red-100 text-red-800',
  EN_ATTENTE_SIGNATURE: 'bg-purple-100 text-purple-800',
  SIGNE: 'bg-green-100 text-green-800',
  ENVOYE: 'bg-indigo-100 text-indigo-800',
  CLASSE: 'bg-gray-100 text-gray-800',
  REJETE: 'bg-red-100 text-red-800',
};

export function WorkflowTransition({
  courrierId,
  currentStatus,
  onTransition,
  disabled = false,
}: WorkflowTransitionProps) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [selectedStatus, setSelectedStatus] = useState<CourrierStatus | ''>('');
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);

  // Mutation pour la transition
  const transitionMutation = useMutation({
    mutationFn: ({ toStatus, comment }: { toStatus: CourrierStatus; comment?: string }) =>
      courrierService.transitionCourrier(courrierId, { to_status: toStatus, comment }),
    onSuccess: (updatedCourrier) => {
      // Invalider les queries pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.detail(courrierId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.statistics() });

      // Notification de succès
      dispatch(addNotification({
        type: 'success',
        title: 'Statut mis à jour',
        message: `Le courrier est maintenant "${STATUS_LABELS[updatedCourrier.status]}"`,
      }));

      // Callback
      onTransition?.(updatedCourrier.status);

      // Réinitialiser le formulaire
      setSelectedStatus('');
      setComment('');
      setShowComment(false);
    },
    onError: (error: any) => {
      dispatch(addNotification({
        type: 'error',
        title: 'Erreur',
        message: error.message || 'Erreur lors de la transition',
      }));
    },
  });

  const availableTransitions = STATUS_TRANSITIONS[currentStatus] || [];

  const handleStatusSelect = (status: CourrierStatus) => {
    setSelectedStatus(status);
    setShowComment(true);
  };

  const handleTransition = () => {
    if (!selectedStatus) return;

    transitionMutation.mutate({
      toStatus: selectedStatus,
      comment: comment.trim() || undefined,
    });
  };

  const handleCancel = () => {
    setSelectedStatus('');
    setComment('');
    setShowComment(false);
  };

  if (availableTransitions.length === 0) {
    return (
      <div className="text-center p-4 text-gray-500">
        <p>Aucune transition disponible depuis l'état "{STATUS_LABELS[currentStatus]}"</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* État actuel */}
      <div className="flex items-center space-x-3">
        <span className="text-sm font-medium text-gray-700">État actuel :</span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[currentStatus]}`}>
          {STATUS_LABELS[currentStatus]}
        </span>
      </div>

      {/* Transitions disponibles */}
      {!showComment ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Transitions disponibles :</p>
          <div className="grid grid-cols-2 gap-2">
            {availableTransitions.map((status) => (
              <Button
                key={status}
                variant="outline"
                size="sm"
                onClick={() => handleStatusSelect(status)}
                disabled={disabled || transitionMutation.isPending}
                className="justify-start"
              >
                {STATUS_LABELS[status]}
              </Button>
            ))}
          </div>
        </div>
      ) : (
        /* Formulaire de commentaire */
        <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Transition vers : <span className={selectedStatus ? STATUS_COLORS[selectedStatus] : ''}>{selectedStatus ? STATUS_LABELS[selectedStatus] : ''}</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={transitionMutation.isPending}
            >
              ✕
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Commentaire (optionnel)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Ajouter un commentaire pour expliquer cette transition..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              disabled={transitionMutation.isPending}
            />
          </div>

          <div className="flex space-x-2">
            <Button
              onClick={handleTransition}
              isLoading={transitionMutation.isPending}
              disabled={transitionMutation.isPending}
            >
              Confirmer la transition
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={transitionMutation.isPending}
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant pour l'historique des transitions
interface WorkflowHistoryProps {
  events: Array<{
    id: number;
    from_status: CourrierStatus;
    to_status: CourrierStatus;
    timestamp: string;
    comment?: string;
    actor_name?: string;
  }>;
  isLoading?: boolean;
}

export function WorkflowHistory({ events, isLoading }: WorkflowHistoryProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <p className="text-gray-500 text-center py-4">
        Aucun événement dans l'historique
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {events.map((event, index) => (
        <div key={event.id || index} className="flex items-start space-x-3">
          {/* Ligne de connexion */}
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            {index < events.length - 1 && (
              <div className="w-px h-8 bg-gray-200 mt-2"></div>
            )}
          </div>

          {/* Contenu de l'événement */}
          <div className="flex-1 pb-4">
            <div className="flex items-center space-x-2 mb-1">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[event.from_status]}`}>
                {STATUS_LABELS[event.from_status]}
              </span>
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[event.to_status]}`}>
                {STATUS_LABELS[event.to_status]}
              </span>
            </div>

            <div className="text-sm text-gray-600">
              <span>{new Date(event.timestamp).toLocaleString('fr-FR')}</span>
              {event.actor_name && (
                <>
                  <span className="mx-1">•</span>
                  <span>par {event.actor_name}</span>
                </>
              )}
            </div>

            {event.comment && (
              <p className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                "{event.comment}"
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}